// src/components/PromoCommentDrawer.js
// Slide-up comment drawer for a reel: lists existing comments (newest first)
// and lets the signed-in user compose a new one. Mirrors the visual language
// of the immersive Reels player (dark, rounded, full-width on mobile).
import React, { useState, useEffect } from 'react';
import { B } from '../constants';
import { promos as promosAPI } from '../api';

export default function PromoCommentDrawer({ promo, user, onAuthRequired, onClose, onCountChange }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    if (!promo) return;
    promosAPI.comments(promo.id)
      .then(data => { if (mounted) setComments(data.comments || []); })
      .catch(() => { if (mounted) setError('Failed to load comments'); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [promo]);

  const submit = async () => {
    const text = body.trim();
    if (!text) return;
    if (!user) { onAuthRequired && onAuthRequired(); return; }
    setPosting(true);
    setError('');
    try {
      const { comment, commentCount } = await promosAPI.addComment(promo.id, text);
      setComments(prev => [comment, ...prev.filter(c => c.id !== comment.id)]);
      setBody('');
      onCountChange?.(promo.id, typeof commentCount === 'number' ? commentCount : (n) => n + 1);
      // Background refresh so comments posted by others meanwhile show up too.
      promosAPI.comments(promo.id)
        .then(data => { if (Array.isArray(data?.comments)) setComments(data.comments); })
        .catch(() => {});
    } catch (e) {
      setError(e.message || 'Failed to post comment');
    } finally {
      setPosting(false);
    }
  };

  const avatar = (c) => c.user?.avatar
    ? <img src={c.user.avatar} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
    : <div style={{ width: 36, height: 36, borderRadius: '50%', background: `linear-gradient(135deg,${B.forest},${B.mint})`, display: 'grid', placeItems: 'center', color: 'white', fontWeight: 800, flexShrink: 0 }}>
        {c.user?.name?.[0] || '?'}
      </div>;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 120, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    }} onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#121d29', width: 'min(560px, 100vw)', maxHeight: '75vh',
          borderTopLeftRadius: 20, borderTopRightRadius: 20, color: 'white',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 800, fontFamily: "'Montserrat',sans-serif", fontSize: 15 }}>💬 Comments</div>
            <div style={{ fontSize: 12, color: B.muted, fontFamily: "'Montserrat',sans-serif" }}>{promo.title}</div>
          </div>
          <button onClick={onClose} aria-label="Close comments" style={{ background: 'none', border: 'none', color: 'white', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0', minHeight: 120 }}>
          {loading ? (
            <div style={{ padding: 30, textAlign: 'center', color: B.muted, fontFamily: "'Montserrat',sans-serif", fontSize: 13 }}>Loading…</div>
          ) : comments.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: B.muted, fontFamily: "'Montserrat',sans-serif", fontSize: 13 }}>
              Be the first to comment ✨
            </div>
          ) : (
            comments.map(c => (
              <div key={c.id} style={{ display: 'flex', gap: 10, padding: '12px 20px' }}>
                {avatar(c)}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: B.mint, fontWeight: 700, fontFamily: "'Montserrat',sans-serif" }}>
                    @{c.user?.username || c.user?.name || 'traveller'}
                    {c.user?.isInfluencer ? ' ✨ CREATOR' : ''}
                  </div>
                  <div style={{ fontSize: 14, lineHeight: 1.4, fontFamily: "'Montserrat',sans-serif", marginTop: 2 }}>{c.body}</div>
                </div>
              </div>
            ))
          )}
          {error && !loading && <div style={{ padding: '4px 20px', color: '#ff6b6b', fontSize: 12, fontFamily: "'Montserrat',sans-serif" }}>{error}</div>}
        </div>

        <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', gap: 10, alignItems: 'center' }}>
          <input
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
            placeholder={user ? 'Add a comment…' : 'Sign in to comment'}
            maxLength={1000}
            style={{
              flex: 1, background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 50,
              padding: '10px 16px', color: 'white', outline: 'none', fontFamily: "'Montserrat',sans-serif", fontSize: 13,
            }}
          />
          <button
            onClick={submit}
            disabled={!body.trim() || posting}
            style={{
              background: `linear-gradient(135deg,${B.amber},#ff8c00)`, color: B.night, border: 'none',
              borderRadius: 50, padding: '10px 20px', fontWeight: 800, cursor: posting ? 'default' : 'pointer',
              fontFamily: "'Montserrat',sans-serif", fontSize: 13,
            }}
          >
            {posting ? '…' : 'Post'}
          </button>
        </div>
      </div>
    </div>
  );
}
